import { isEmpty } from 'lodash'
import { useEffect } from 'react'
import {
  StackCardInterpolatedStyle,
  StackCardInterpolationProps,
  StackNavigationOptions,
} from '@react-navigation/stack'

const OVERLAY_BACKGROUND_COLOR = '#0A0A0CB3' // 'theme.solid.black' with 0.7 opacity

export const modalWithOverlayOptions: StackNavigationOptions = {
  cardOverlayEnabled: true,
  presentation: 'transparentModal',
}

export const tutorialModalOptions: StackNavigationOptions = {
  ...modalWithOverlayOptions,
  cardStyleInterpolator: ({ current: { progress } }) => ({
    cardStyle: {
      opacity: progress.interpolate({
        inputRange: [0, 0.5, 0.9, 1],
        outputRange: [0, 0.25, 0.7, 1],
      }),
    },
    overlayStyle: {
      opacity: progress.interpolate({
        inputRange: [0, 1],
        outputRange: [0, 0.7],
        extrapolate: 'clamp',
      }),
    },
  }),
}

export const isModified = (sort: any): boolean => {
  const { types, query, startHour, endHour, order } = sort || {}

  return (
    (query && !isEmpty(query)) ||
    (types && !isEmpty(types)) ||
    (startHour && startHour !== 0) ||
    (endHour && endHour !== 24) ||
    (order && order !== 'descending') ||
    false
  )
}

const forFadeFromBottomInterpolator = ({
  current,
  layouts: { screen },
}: StackCardInterpolationProps): StackCardInterpolatedStyle => {
  const translateY = current.progress.interpolate({
    inputRange: [0, 1],
    outputRange: [screen.height, 0],
  })

  const opacity = current.progress.interpolate({
    inputRange: [0, 1],
    outputRange: [0, 1],
  })

  return {
    overlayStyle: {
      opacity,
      backgroundColor: OVERLAY_BACKGROUND_COLOR,
    },
    cardStyle: {
      transform: [{ translateY }],
    },
  }
}

const fadeInInterpolator = ({
  current,
}: StackCardInterpolationProps): StackCardInterpolatedStyle => {
  const opacity = current.progress.interpolate({
    inputRange: [0, 1],
    outputRange: [0, 1],
  })
  const delayedOpacity = current.progress.interpolate({
    inputRange: [0, 0, 0, 0, 1],
    outputRange: [0, 0, 0, 0, 1],
  })

  return {
    overlayStyle: {
      opacity,
      backgroundColor: OVERLAY_BACKGROUND_COLOR,
    },
    cardStyle: {
      opacity: delayedOpacity,
    },
  }
}

export const fadeFromBottomModalOptions: StackNavigationOptions = {
  ...modalWithOverlayOptions,
  gestureEnabled: false,
  cardStyleInterpolator: forFadeFromBottomInterpolator,
}

export const fadeInModalOptions: StackNavigationOptions = {
  ...modalWithOverlayOptions,
  gestureEnabled: false,
  cardStyleInterpolator: fadeInInterpolator,
}

export const gestureDismissibleModalOptions: StackNavigationOptions = {
  ...fadeFromBottomModalOptions,
  gestureEnabled: true,
}

export const LAZY_SCREEN_PATHS = {
  ActionGoalFormModal: () =>
    import('@src/screens/ActionGoals/screens/ActionGoalFormModal').then((m) => ({
      default: m.ActionGoalFormModal,
    })),
  ActionGoalListScreen: () =>
    import('@src/screens/ActionGoals/screens/ActionGoalList').then((m) => ({
      default: m.ActionGoalList,
    })),
  AdminSettings: () =>
    import('@src/screens/Settings/containers/AdminSettings').then((m) => ({
      default: m.AdminSettingsContainer,
    })),
  AppExplorationCarousel: () =>
    import('@src/screens/Onboarding/screens/AppExplorationCarousel').then((m) => ({
      default: m.AppExplorationCarousel,
    })),
  AppExplorationPrompt: () =>
    import('@src/screens/Onboarding/screens/AppExplorationPrompt').then((m) => ({
      default: m.AppExplorationPrompt,
    })),
  BarcodeIngredientSearch: () =>
    import('@src/screens/Meals/containers/BarcodeIngredientSearch').then((m) => ({
      default: m.BarcodeIngredientSearchContainer,
    })),
  Camera: () => import('@src/screens/Common/containers/CameraContainer'),
  CoachInfoModal: () =>
    import('@src/screens/NutritionistHub/components/CareTeamCard/CoachInfoModal').then((m) => ({
      default: m.CoachInfoModal,
    })),
  CancelSubscriptionConfirmation: () =>
    import('@src/screens/Marketplace/components/CancelSubscriptionConfirmation').then((m) => ({
      default: m.CancelSubscriptionConfirmation,
    })),
  CancelSubscriptionOnCommitment: () =>
    import('@src/screens/Marketplace/components/CancelSubscriptionOnCommitment').then((m) => ({
      default: m.CancelSubscriptionOnCommitment,
    })),
  CancelSubscriptionOtherOptions: () =>
    import('@src/screens/Marketplace/components/CancelSubscriptionOtherOptions').then((m) => ({
      default: m.CancelSubscriptionOtherOptions,
    })),
  ChangeBillingCycleModal: () =>
    import('@src/screens/Marketplace/components/BillingCycle/ChangeBillingCycle').then((m) => ({
      default: m.ChangeBillingCycle,
    })),
  ChangeBillingCycleSuccessModal: () =>
    import('@src/screens/Marketplace/components/BillingCycle/ChangeBillingCycleSuccess').then(
      (m) => ({
        default: m.ChangeBillingCycleSuccess,
      }),
    ),
  Chat: () => import('@src/screens/Chat').then((m) => ({ default: m.ChatScreen })),
  ChurnRecoveryMembershipOffer: () =>
    import(
      '@src/screens/Marketplace/components/ChurnRecovery/ChurnRecoveryMembershipOffer'
    ).then((m) => ({ default: m.ChurnRecoveryMembershipOffer })),
  ChurnMonthlyCGMOffer: () =>
    import('@src/screens/Marketplace/components/ChurnRecovery/ChurnMonthlyCGMOffer').then((m) => ({
      default: m.ChurnMonthlyCGMOffer,
    })),
  ChurnRecoveryOptions: () =>
    import('@src/screens/Marketplace/components/ChurnRecovery/ChurnRecoveryOptions').then((m) => ({
      default: m.ChurnRecoveryOptions,
    })),
  ChurnRecoveryProactiveUpsellOffer: () =>
    import(
      '@src/screens/Marketplace/components/ChurnRecovery/ProactiveUpsellOffer/ChurnRecoveryProactiveUpsellOffer'
    ).then((m) => ({
      default: m.ChurnRecoveryProactiveUpsellOffer,
    })),
  ChurnRecoveryUpsellOffer: () =>
    import(
      '@src/screens/Marketplace/components/ChurnRecovery/UpsellOffer/ChurnRecoveryUpsellOffer'
    ).then((m) => ({
      default: m.ChurnRecoveryUpsellOffer,
    })),
  ChurnRecoveryUpsellOfferConfirmation: () =>
    import(
      '@src/screens/Marketplace/components/ChurnRecovery/UpsellOffer/ChurnRecoveryUpsellOfferConfirmation'
    ).then((m) => ({ default: m.ChurnRecoveryUpsellOfferConfirmation })),
  CoachSwitchSummaryScreen: () =>
    import(
      '@src/screens/NutritionistHub/components/NutritionistSwitch/CoachSwitchSummaryScreen'
    ).then((m) => ({ default: m.CoachSwitchSummaryScreen })),
  ConfirmWeightLossRecurrence: () =>
    import('@src/screens/NutritionistHub/components/ConfirmWeightLossRecurrenceScreen').then(
      (m) => ({
        default: m.ConfirmWeightLossRecurrenceScreen,
      }),
    ),
  LabResultDetail: () =>
    import('@src/screens/LabResults/screens/LabResultDetailScreen').then((m) => ({
      default: m.LabResultDetailScreen,
    })),
  Legal: () =>
    import('@src/screens/PolicyConsents/components/Legal').then((m) => ({
      default: m.Legal,
    })),
  OpenSourceLicenses: () =>
    import('@src/screens/PolicyConsents/components/OpenSourceLicenses').then((m) => ({
      default: m.OpenSourceLicenses,
    })),
  OpenSourceLicenseDetail: () =>
    import('@src/screens/PolicyConsents/components/OpenSourceLicenseDetail').then((m) => ({
      default: m.OpenSourceLicenseDetail,
    })),
  ManageUpcomingSubscriptionModal: () =>
    import('@src/screens/Marketplace/components/ManageUpcomingSubscription').then((m) => ({
      default: m.ManageUpcomingSubscription,
    })),
  ManageSubscriptions: () =>
    import('@src/screens/Marketplace/components/ProgramDetails/ManageSubscriptions').then((m) => ({
      default: m.ManageSubscriptions,
    })),
  NutritionistSwitchModal: () =>
    import(
      '@src/screens/NutritionistHub/components/NutritionistSwitch/CoachSwitchModal'
    ).then((m) => ({ default: m.CoachSwitchModal })),
  CoachSwitchSpecialtiesFilterModal: () =>
    import(
      '@src/screens/NutritionistHub/components/NutritionistSwitch/CoachSwitchSpecialtiesFilterModal'
    ).then((m) => ({
      default: m.CoachSwitchSpecialtiesFilterModal,
    })),
  OnboardingNavigator: () =>
    import('@src/navigation/navigators/onboarding.navigator').then((m) => ({
      default: m.OnboardingNavigator,
    })),
  OutdatedScreen: () =>
    import('@src/screens/Common/Outdated').then((m) => ({ default: m.OutdatedScreen })),
  Questionnaire: () =>
    import('@src/screens/Questionnaire/questionnaire.navigator').then((m) => ({
      default: m.QuestionnaireNavigator,
    })),
  InsuranceQuestionnaire: () =>
    import('@src/screens/InsuranceQuestionnaire').then((m) => ({
      default: m.InsuranceQuestionnaireNavigator,
    })),
  ReactivateSubscriptionModal: () =>
    import('@src/screens/Marketplace/components/ReactivateSubscription').then((m) => ({
      default: m.ReactivateSubscription,
    })),
  ResumeSubscriptionModal: () =>
    import('@src/screens/Marketplace/components/PauseSubscription/ResumeSubscription').then(
      (m) => ({
        default: m.ResumeSubscription,
      }),
    ),
  CoachSwitchSeeAllCoachesScreen: () =>
    import(
      '@src/screens/NutritionistHub/components/NutritionistSwitch/CoachSwitchSeeAllCoachesScreen'
    ).then((m) => ({
      default: m.CoachSwitchSeeAllCoachesScreen,
    })),
  CallSchedulingSeeAllCoachesScreen: () =>
    import('@src/screens/NutritionistHub/components/CallSchedulingSeeAllCoachesScreen').then(
      (m) => ({
        default: m.CallSchedulingSeeAllCoachesScreen,
      }),
    ),
  SupportAnswer: () =>
    import('@src/screens/Profile/components/SupportHub/SupportArticleContainer').then((m) => ({
      default: m.SupportArticleContainer,
    })),
  SupportArchive: () =>
    import('@src/screens/Profile/components/SupportHub/SupportArchiveContainer').then((m) => ({
      default: m.SupportArchiveContainer,
    })),
  SupportHub: () =>
    import('@src/screens/Profile/containers/SupportHub').then((m) => ({
      default: m.SupportHub,
    })),
  SteloPaymentSuccessful: () =>
    import('@src/screens/SignUp/components/SteloPaymentSuccessful').then((m) => ({
      default: m.SteloPaymentSuccessful,
    })),
  StressManagementIntroScreen: () =>
    import(
      '@src/screens/NutritionistHub/components/StressManagement/StressManagementIntroScreen'
    ).then((m) => ({
      default: m.StressManagementIntroScreen,
    })),
  ScheduleNextCallModal: () =>
    import('@src/screens/NutritionistHub/components/ScheduleNextCall/ScheduleNextCallModal').then(
      (m) => ({
        default: m.ScheduleNextCallModal,
      }),
    ),
  UnifiedOnboardingAppointmentOfferConfirmation: () =>
    import('@src/screens/UpsellOffer/AppointmentOfferConfirmation').then((m) => ({
      default: m.UnifiedOnboardingAppointmentOfferConfirmation,
    })),
  UnifiedOnboardingConfirmation: () =>
    import('@src/screens/Onboarding/screens/UnifiedOnboardingConfirmation').then((m) => ({
      default: m.UnifiedOnboardingConfirmation,
    })),
  UpsellOffer: () =>
    import('@src/screens/UpsellOffer/UpsellOffer').then((m) => ({
      default: m.UpsellOffer,
    })),
  UpsellOfferConfirmation: () =>
    import('@src/screens/UpsellOffer/UpsellOfferConfirmation').then((m) => ({
      default: m.UpsellOfferConfirmation,
    })),
  VideoCallConfirmation: () =>
    import('@src/screens/NutritionistHub/components/VideoCallConfirmationScreen').then((m) => ({
      default: m.VideoCallConfirmation,
    })),
  VideoCallConfirmed: () =>
    import('@src/screens/NutritionistHub/components/VideoCallConfirmedScreen').then((m) => ({
      default: m.VideoCallConfirmed,
    })),
  LabPanelsScreen: () =>
    import('@screens/NutritionistHub/components/LabPanels/LabPanelsScreen').then((m) => ({
      default: m.LabPanelsScreen,
    })),
  LabPanelsOrderConfirmed: () =>
    import('@screens/NutritionistHub/components/LabPanels/LabPanelsOrderConfirmed').then((m) => ({
      default: m.LabPanelsOrderConfirmed,
    })),
  LabPanelsUpsellModal: () =>
    import('@screens/Home/components/LabPanelsUpsellModal').then((m) => ({
      default: m.LabPanelsUpsellModal,
    })),
  FreeCallTrialUpsellPopup: () =>
    import('@screens/Home/components/FreeCallTrialUpsellPopup').then((m) => ({
      default: m.FreeCallTrialUpsellPopup,
    })),
  CoachingCallCreditBalanceModal: () =>
    import('@screens/NutritionistHub/components/CoachingCallCreditBalanceModal').then((m) => ({
      default: m.CoachingCallCreditBalanceModal,
    })),
  CallSchedulingReviewScreen: () =>
    import('@screens/NutritionistHub/components/CallScheduling/ReviewScreen').then((m) => ({
      default: m.ReviewScreen,
    })),
  CancelRecurringAppointmentModal: () =>
    import('@screens/NutritionistHub/components/CancelRecurringAppointmentModal').then((m) => ({
      default: m.CancelRecurringAppointmentModal,
    })),
  MicrobiomeTest: () =>
    import('@screens/NutritionistHub/components/MicrobiomeTest/MicrobiomeTest').then((m) => ({
      default: m.MicrobiomeTest,
    })),
  LabPanelRecurrence: () =>
    import('@screens/NutritionistHub/components/LabPanels/LabPanelRecurrenceScreen').then((m) => ({
      default: m.LabPanelRecurrenceScreen,
    })),
  UpsertQuickPromptModal: () =>
    import('@screens/Nora/screens/UpsertQuickPromptModal').then((m) => ({
      default: m.UpsertQuickPromptModal,
    })),
  ReportNoteModal: () =>
    import('@screens/Nora/screens/ReportNoteModal').then((m) => ({
      default: m.ReportNoteModal,
    })),
  CustomReportModal: () =>
    import('@screens/Nora/screens/CustomReportModal').then((m) => ({
      default: m.CustomReportModal,
    })),
  NoraConversationScreen: () =>
    import('@screens/Nora/screens').then((m) => ({
      default: m.NoraConversationScreen,
    })),
  NoraConversationsScreen: () =>
    import('@screens/Nora/screens').then((m) => ({
      default: m.NoraConversationsScreen,
    })),
  NoraReportScreen: () =>
    import('@screens/Nora/screens').then((m) => ({
      default: m.NoraReportScreen,
    })),
  NoraQuickPromptsScreen: () =>
    import('@screens/Nora/screens').then((m) => ({
      default: m.NoraQuickPromptsScreen,
    })),
  NoraReportsScreen: () =>
    import('@screens/Nora/screens').then((m) => ({
      default: m.NoraReportsScreen,
    })),
  NoraChatPreferencesSettingsScreen: () =>
    import('@screens/Nora/screens/NoraChatPreferencesSettingsScreen').then((m) => ({
      default: m.NoraChatPreferencesSettingsScreen,
    })),
  NoraNotificationSettingsScreen: () =>
    import('@screens/Nora/screens/NoraNotificationSettingsScreen').then((m) => ({
      default: m.NoraNotificationSettingsScreen,
    })),
  NoraSettingsScreen: () =>
    import('@screens/Nora/screens').then((m) => ({
      default: m.NoraSettingsScreen,
    })),
}

export const useLazyScreenPreload = (
  screen: keyof typeof LAZY_SCREEN_PATHS,
  options?: { shouldPreload?: boolean },
) => {
  const { shouldPreload = true } = options || {}

  useEffect(() => {
    if (shouldPreload) {
      const preload = LAZY_SCREEN_PATHS[screen]
      preload()
    }
  }, [screen, shouldPreload])
}
